import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Users, Building2, Mail, TrendingUp, IndianRupee, CheckCircle, Clock, Target,
} from 'lucide-react';
import CrmCharts from './CrmCharts';

function StatCard({ title, value, icon: Icon, color, to }) {
  const inner = (
    <Card className="border-border hover:shadow-md transition-shadow">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value ?? '—'}</p>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function CrmDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role_slug === 'super_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/crm/dashboard')
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading dashboard…</div>;
  }

  const stats = data?.stats || {};
  const charts = data?.charts || {};

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-foreground">CRM Dashboard</h1>
        <p className="text-muted-foreground">
          {isAdmin ? 'Platform sales overview' : 'Your performance overview'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {isAdmin && (
          <>
            <StatCard title="Total Schools" value={stats.total_schools} icon={Building2} color="bg-blue-600" to="/schools" />
            <StatCard title="Total Employees" value={stats.total_employees} icon={Users} color="bg-indigo-600" to="/crm/employees" />
            <StatCard title="Active Employees" value={stats.active_employees} icon={CheckCircle} color="bg-green-600" />
            <StatCard title="Monthly Revenue" value={stats.monthly_revenue != null ? `₹${stats.monthly_revenue}` : '—'} icon={IndianRupee} color="bg-emerald-600" />
          </>
        )}
        <StatCard title="Total Inquiries" value={stats.total_inquiries} icon={Mail} color="bg-violet-600" to="/crm/inquiries" />
        <StatCard title="Today's Inquiries" value={stats.today_inquiries} icon={Mail} color="bg-purple-600" />
        <StatCard title="Weekly Inquiries" value={stats.weekly_inquiries} icon={TrendingUp} color="bg-sky-600" />
        <StatCard title="Monthly Inquiries" value={stats.monthly_inquiries} icon={TrendingUp} color="bg-cyan-600" />
        <StatCard title="Schools Joined" value={stats.total_schools_joined} icon={Building2} color="bg-teal-600" />
        <StatCard title="Pending Follow-ups" value={stats.pending_followups} icon={Clock} color="bg-amber-600" to="/crm/inquiries" />
        <StatCard title="Closed Inquiries" value={stats.closed_inquiries} icon={CheckCircle} color="bg-slate-600" />
        {!isAdmin && (
          <StatCard title="Incentives Earned" value={stats.total_incentives_earned != null ? `₹${stats.total_incentives_earned}` : '—'} icon={IndianRupee} color="bg-green-600" />
        )}
        {isAdmin && (
          <StatCard title="This Month Joins" value={stats.this_month_joins} icon={Target} color="bg-orange-600" />
        )}
      </div>

      <CrmCharts charts={charts} />

      {data?.recent_activities?.length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.recent_activities.map((a) => {
                const performerName = [a.performed_by_first_name, a.performed_by_last_name]
                  .filter(Boolean)
                  .join(' ');
                const subjectName = [a.first_name, a.last_name].filter(Boolean).join(' ');
                const byLabel = performerName
                  ? ` (by ${performerName})`
                  : subjectName
                    ? ` (${subjectName})`
                    : '';
                return (
                <li key={a.id} className="flex justify-between text-sm border-b border-border pb-2">
                  <span>
                    <span className="font-medium">{a.action?.replace(/_/g, ' ')}</span>
                    {a.details ? ` — ${a.details}` : ''}
                    {byLabel}
                  </span>
                  <span className="text-muted-foreground">{a.created_at}</span>
                </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
